import { Button, Flex } from '@aws-amplify/ui-react';
import type { QueryCondition } from '@edulead/models';
import type { Edulead } from 'edulead';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomList from '../custom-components/CustomList';
import LoadingBars from '../custom-components/LoadingBars';
import NothingHere from '../custom-components/NothingHere';
import { GlobalContext } from '../global-context';
import { usePermission } from '../hooks/usePermission';
import { applyBeaconIds } from './navigator/beacon-ids';
import { allPages, toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { SortOptions } from '../utils/CustomTypes';
import General from '../utils/GeneralUtils';
import {
  institutionOrder,
  legacyInstitutionsResponse,
  LegacyInstitutionsResponse,
} from '../utils/legacyInstitution';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';
import { handleNewExport } from '../utils/newExportFeature';
import { TokenPagination } from './TokenPagination';

const PAGE_SIZE = 25;

export interface InstitutionsResultsProps {
  /** The conditions the API runs, as the search sends them (the export sends the same). */
  conditions: QueryCondition[];
  /**
   * What identifies the search apart from its page and sort: a change empties the selection
   * and shows the full loading state, where a page or sort change shows an overlay.
   */
  searchKey: string;
  /** The console page the search ran on, for the search history; none records nothing. */
  source?: Edulead.SearchInstitutionsRequestSource;
  /** The saved search the export comes from, when known. */
  savedSearchId?: string;
  /** Told as a load starts and ends (a page or sort change too), for a button's spinner. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Whether the list's toolbar and column header stay at the top while it scrolls. */
  stickyHeader?: boolean;
}

/**
 * The institutions a search returns, paged: the list with its sort, selection, export and Add
 * to Beacon, and the pager. The page (`page`, `pageToken`) and sort (`sortBy`, `sortDir`) are
 * the URL's, so a refresh or a shared link keeps its place; the other query params are left as
 * they are. The Institutions directory and the Navigator's Institutions view both show this.
 */
export function InstitutionsResults({
  conditions,
  searchKey,
  source,
  savedSearchId,
  onLoadingChange,
  stickyHeader,
}: InstitutionsResultsProps) {
  const globalContext = useContext(GlobalContext);
  const institution = globalContext.environment.entities.institution;
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const page = parseInt(params.get('page') ?? '') || 1;
  const pageToken = params.get('pageToken') || undefined;
  const sortBy = params.get('sortBy') || 'name';
  const sortDir: 'asc' | 'desc' = params.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const sortKey = `${searchKey}|${sortBy}|${sortDir}`;

  const [isLoading, setIsLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [response, setResponse] = useState<LegacyInstitutionsResponse | null>(null);
  const [nextPageToken, setNextPageToken] = useState('');
  /** The tokens of the pages seen of the current search (tokens[i] loads page i + 1), for Previous. */
  const pageTokens = useRef<{ search: string; tokens: string[] }>({ search: '', tokens: [''] });
  const [isExpanded, setIsExpanded] = useState(
    LocalStorageService.get(LocalStorageKeys.IS_INSTITUTIONS_LIST_EXPANDED) === 'true',
  );
  const [selectedRows, setSelectedRows] = useState<{ [key: string]: any }>({});
  /** The search the shown rows are of; another search loads in full, a page or sort as an overlay. */
  const shownSearch = useRef<string | null>(null);

  // Add to Beacon, for those who may change the team's beacons.
  const { hasPermission } = usePermission();
  const canUseBeacons = hasPermission('beacons:manage');
  const [isAddToBeaconOpen, setIsAddToBeaconOpen] = useState(false);
  const [isLoadingBeacons, setIsLoadingBeacons] = useState(false);
  /** The ids the Add to Beacon button was clicked with. */
  const [idsForBeacon, setIdsForBeacon] = useState<string[]>([]);
  const [beacons, setBeacons] = useState<Edulead.EduleadV4Beacon[]>([]);
  const [isAddingToBeacon, setIsAddingToBeacon] = useState(false);

  useEffect(() => {
    const isNewSearch = shownSearch.current !== searchKey;
    shownSearch.current = searchKey;
    if (isNewSearch) setSelectedRows({});
    load(isNewSearch || !response?.data?.data?.length);
  }, [location.search, searchKey]);

  useEffect(() => {
    if (canUseBeacons) loadBeacons();
  }, [canUseBeacons]);

  async function load(inFull: boolean) {
    try {
      if (inFull) setIsLoading(true);
      else setIsPaginationLoading(true);
      onLoadingChange?.(true);
      if (pageTokens.current.search !== sortKey) {
        pageTokens.current = { search: sortKey, tokens: [''] };
      }
      if (pageToken) {
        pageTokens.current.tokens[page - 1] = pageToken;
      }
      const result = await eduleadV4.institutions.search({
        conditions: JSON.stringify(conditions),
        source,
        order_by: institutionOrder(sortBy, sortDir),
        page_size: PAGE_SIZE,
        page_token: pageToken,
      });
      const next = result.response.next_page_token ?? '';
      if (next) {
        pageTokens.current.tokens[page] = next;
      }
      setNextPageToken(next);
      setResponse(legacyInstitutionsResponse(result.data, result.response.total_size));
    } catch (err) {
      toastV4Error(err, 'Something went wrong loading institutions');
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
      onLoadingChange?.(false);
    }
  }

  async function loadBeacons() {
    try {
      setIsLoadingBeacons(true);
      // A team holds at most its NR_BEACONS__TOTAL (200 unless raised): two pages at most.
      setBeacons(await allPages(eduleadV4.beacons.list({ page_size: 100, order_by: 'name' })));
    } catch (error) {
      toastV4Error(error, 'Failed to load beacons');
    } finally {
      setIsLoadingBeacons(false);
    }
  }

  /** Adds the clicked-with ids to the beacon; the results on screen do not change. */
  async function addToBeacon(beacon: Edulead.EduleadV4Beacon) {
    if (!beacon.id) return;
    const count = idsForBeacon.length;
    try {
      setIsAddingToBeacon(true);
      await applyBeaconIds(beacon.id, idsForBeacon, 'Added');
      toast.success(
        `Added ${count.toLocaleString()} ${count === 1 ? 'institution' : 'institutions'} to ${beacon.name}`,
      );
      setIsAddToBeaconOpen(false);
      setSelectedRows({});
    } catch (error) {
      toastV4Error(error, 'Failed to add institutions');
    } finally {
      setIsAddingToBeacon(false);
      try {
        const { beacon: fresh } = await eduleadV4.beacons.get({ id: beacon.id });
        if (fresh) setBeacons((prev) => prev.map((b) => (b.id === fresh.id ? fresh : b)));
      } catch {
        // The count on the button is a convenience; the next load of the page refreshes it.
      }
    }
  }

  /** Goes to a page of the search, keeping the URL's other params. */
  const goTo = (nextPage: number, token: string, sort?: SortOptions) => {
    const next = new URLSearchParams(location.search);
    next.set('page', String(nextPage));
    if (token) next.set('pageToken', token);
    else next.delete('pageToken');
    if (sort) {
      next.set('sortBy', sort.sortBy ?? '');
      next.set('sortDir', sort.sortDir ?? '');
    }
    navigate(`${location.pathname}?${next.toString()}`);
  };

  const onSort = (sort: SortOptions) => goTo(1, '', sort);

  // Next and Previous keep the page's token in the URL; a token not seen in this visit (a link to
  // a later page) sends Previous to page 1.
  const onPrevious = () => {
    const previous = page - 1;
    if (previous < 1) return;
    const token = previous === 1 ? '' : pageTokens.current.tokens[previous - 1];
    if (token === undefined) goTo(1, '');
    else goTo(previous, token);
  };

  const onNewExport = useCallback(
    async (config: { type: 'selected' | 'all'; ids?: string[] }) => {
      // For selected items, use ids.length; for all, use total from search response
      const estimatedCount =
        config.type === 'selected' && config.ids?.length
          ? config.ids.length
          : response?.data?.total || 0;
      const exportId = await handleNewExport({
        ...config,
        entity: 'INSTITUTION',
        searchParams: { pageSize: PAGE_SIZE },
        query: conditions,
        estimatedCount,
        savedSearchId,
      });
      navigate(`/exports/${exportId}/target`);
    },
    [conditions, navigate, response, savedSearchId],
  );

  if (isLoading) {
    return (
      <div className="content content-1000">
        <LoadingBars numberOfRows={6} />
      </div>
    );
  }
  if (!response?.data?.data?.length) {
    return (
      <div className="content content-1000">
        <NothingHere text="No institutions found..." />
      </div>
    );
  }

  return (
    <div className={`content ${isExpanded ? 'content-1600' : 'content-1000'}`}>
      <CustomList
        searchResponse={response}
        isLoading={isLoading}
        isPaginationLoading={isPaginationLoading}
        isExpanded={isExpanded}
        stickyHeader={stickyHeader}
        onExpandToggle={() => {
          const next = !isExpanded;
          setIsExpanded(next);
          LocalStorageService.set(LocalStorageKeys.IS_INSTITUTIONS_LIST_EXPANDED, next);
        }}
        isExporting={false}
        showAddToBeaconButton={canUseBeacons}
        onAddItemsToBeacon={(ids) => {
          setIdsForBeacon(ids);
          setIsAddToBeaconOpen(true);
        }}
        onSort={onSort}
        currentSort={{ sortBy, sortDir }}
        onExport={() => {}}
        onNewExport={onNewExport}
        tableFields={institution.tableFields}
        entity={institution.type}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
      />
      <Flex marginTop={30} marginBottom={50} justifyContent="center">
        <TokenPagination
          page={page}
          pageSize={PAGE_SIZE}
          shown={response.data.data.length}
          total={response.data.total}
          hasNext={nextPageToken !== ''}
          isLoading={isPaginationLoading}
          onPrevious={onPrevious}
          onNext={() => goTo(page + 1, nextPageToken)}
        />
      </Flex>
      <ReactModal
        isOpen={isAddToBeaconOpen}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => setIsAddToBeaconOpen(false)}
        style={General.getModalDefaults({ padding: '40px', width: '400px' })}
        closeTimeoutMS={200}
        parentSelector={General.getModalRoot}
      >
        <h4 style={{ marginTop: 0, marginBottom: 20 }}>
          Add {idsForBeacon.length} {idsForBeacon.length === 1 ? 'institution' : 'institutions'} to
          the following beacon
        </h4>
        {isLoadingBeacons ? (
          <LoadingBars />
        ) : beacons.length ? (
          <div className="big-buttons">
            {beacons.map((beacon) => (
              <Button
                className="big-button"
                isDisabled={isAddingToBeacon}
                key={beacon.id}
                onClick={() => addToBeacon(beacon)}
              >
                {beacon.name} ({(beacon.institution_count ?? 0).toLocaleString()})
              </Button>
            ))}
          </div>
        ) : (
          <div>
            No beacons yet. <Link to="/navigator/beacons/map">Create one</Link> in the Navigator.
          </div>
        )}
      </ReactModal>
    </div>
  );
}
